import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "./AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // useRef flag set synchronously — protects against StrictMode double-invocation.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await apiClient.post("/auth/session", {
          session_id: sessionId,
        });
        setUser({
          user_id: res.data.user_id,
          email: res.data.email,
          name: res.data.name,
          picture: res.data.picture,
          role: res.data.role,
        });
        // Strip the fragment, land on dashboard.
        window.history.replaceState({}, "", "/dashboard");
        navigate("/dashboard", { replace: true, state: { user: res.data } });
      } catch (e) {
        console.error("auth/session failed", e);
        setError(
          e?.response?.data?.detail || e?.message || "Authentication failed",
        );
      }
    })();
  }, [navigate, setUser]);

  return (
    <main
      data-testid="auth-callback"
      className="flex min-h-screen items-center justify-center bg-white px-6 text-zinc-900"
    >
      <div className="max-w-md text-center">
        <div
          className="mx-auto mb-6 h-10 w-10 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-900"
          data-testid="auth-callback-spinner"
        />
        <p className="text-sm text-zinc-600">
          {error ? "Sign-in failed" : "Completing sign-in…"}
        </p>
        {error && (
          <p
            data-testid="auth-callback-error"
            className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
          >
            {error}
          </p>
        )}
        {error && (
          <button
            data-testid="auth-callback-back-to-login"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-6 rounded-full bg-zinc-900 px-5 py-2 text-sm font-medium text-white hover:bg-zinc-800"
          >
            Back to sign-in
          </button>
        )}
      </div>
    </main>
  );
}
